'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { Text } from '@ironhack/design-system2/components/text';
import { backgroundToTokens } from '@ironhack/design-system2/utils';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { values } from 'ramda';
import { type ReactElement, type ToggleEvent, useId, useState } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { Button } from '@/app/components/client/button';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoModuleFormat,
} from '@/lib/datocms/types';
import { courseToBackground } from '@/lib/utils';
import { AccordionCard } from './accordion-card';

export type Accordion = DatoBlock<{
  button: DatoButtonBlock | null;
  cards: AccordionCard[];
  description: StructuredTextGraphQlResponse;
  moduleFormat: DatoModuleFormat;
  title: string;
}>;

const trackBackgrounds = values(courseToBackground);

type Props = { data: Accordion } & BoxProps;

// Ref needed for secondary navigation (NavigationTabs)
export const Accordion = forwardRef((props: Props, ref): ReactElement => {
  const {
    data: { button, cards, description, moduleFormat, title },
    title: navTitle,
    ...boxProps
  } = props;
  const { background } = moduleFormat;

  const { color } = backgroundToTokens(background);

  // Replace invalid characters for CSS counters (see link)
  // https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Values/custom-ident#syntax
  const accordionId = useId().replace(/[^\w-]/gu, '__');

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column', gap: 3 }}
      moduleFormat={moduleFormat}
      outerProps={boxProps}
      ref={ref}
      title={navTitle}
    >
      {(title || !isEmptyDocument(description)) && (
        <Flex direction="column" gap={2}>
          {title && (
            <Text as="h2" textStyle="3xl">
              {title}
            </Text>
          )}

          {!isEmptyDocument(description) && (
            <StructuredContent color={color} data={description} />
          )}
        </Flex>
      )}

      <Flex
        align={[null, null, 'start']}
        direction={['column', null, 'row']}
        gap={2}
        sx={{ counterReset: `accordion-${accordionId}` }}
      >
        {cards.map(
          (card: AccordionCard, index: number): ReactElement => (
            <AccordionCard
              data={card}
              key={card.id}
              name={`accordion-${accordionId}`} // Same name allows only 1 open
              onToggle={(event: ToggleEvent<HTMLDetailsElement>) => {
                const { open } = event.currentTarget;
                if (open) setOpenIndex(index);
                // When a card is opened another is closed (multiple events)
                else setOpenIndex((prev) => (prev === index ? null : prev));
              }}
              open={openIndex === index}
              {...(trackBackgrounds.includes(background) && {
                defaultLogoBackground: `light-${background}`,
              })}
            />
          )
        )}
      </Flex>

      {button && (
        <Button
          alignSelf="center"
          mt={[1, null, 2]} // This is in addition the gap
          width={['100%', null, 'auto']}
          {...button}
        />
      )}
    </ModuleWrapper>
  );
});
